import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

const TENANT_PLANS = [
  { value: 'free', label: 'フリー' },
  { value: 'basic', label: 'ベーシック' },
  { value: 'professional', label: 'プロフェッショナル' },
  { value: 'enterprise', label: 'エンタープライズ' },
];

function TenantNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    description: '',
    admin_email: '',
    plan: 'basic',
    max_users: 10,
    max_storage_gb: 1,
  });

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.name || !formData.subdomain || !formData.admin_email) {
        setError('テナント名、サブドメイン、管理者メールは必須です');
        return;
      }

      const result = await apiService.createTenant(formData);
      console.log('Tenant created:', result);
      
      // Navigate to tenant detail page
      if (result && typeof result === 'object' && 'id' in result) {
        navigate(`/tenants/${result.id}`);
      } else {
        navigate('/tenants');
      }
    } catch (error: any) {
      console.error('Failed to create tenant:', error);
      setError(error.response?.data?.detail || 'テナントの作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        新規テナント作成
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="テナント名"
              value={formData.name}
              onChange={handleChange('name')}
              disabled={loading}
              helperText="例: 株式会社サンプル"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="サブドメイン"
              value={formData.subdomain}
              onChange={handleChange('subdomain')}
              disabled={loading}
              helperText="例: sample-company (英数字とハイフンのみ)"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="説明"
              value={formData.description}
              onChange={handleChange('description')}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="管理者メールアドレス"
              value={formData.admin_email}
              onChange={handleChange('admin_email')}
              disabled={loading}
              helperText="このメールアドレスがテナント管理者になります"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="プラン"
              value={formData.plan}
              onChange={handleChange('plan')}
              disabled={loading}
            >
              {TENANT_PLANS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="最大ユーザー数"
              value={formData.max_users}
              onChange={handleChange('max_users')}
              disabled={loading}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="最大ストレージ (GB)"
              value={formData.max_storage_gb}
              onChange={handleChange('max_storage_gb')}
              disabled={loading}
              InputProps={{ inputProps: { min: 0.1, step: 0.1 } }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/tenants')}
            disabled={loading}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? '作成中...' : 'テナントを作成'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default TenantNew;